<template>
  <bm-dialog
    ref="bmDialogRef"
    :visible="value"
    :label="isNewTask ? $m.tasks_new_task_title : $m.tasks_edit_task_title"
    @accept="addOrUpdateTask"
    @cancel="cancel"
  >
    <v-container fluid pa-0 data-qa="add_edit_task_dialog">
      <v-row v-if="taskIsCancelledOrComplete">
        <v-col cols="12">
          <bm-alert type="error">
            {{ taskCancelledOrCompleteAlertText }}
          </bm-alert>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <bm-text-area
            v-model="internalTask.description"
            :t="$m.keys.tasks_description"
            :disabled="taskIsCancelledOrComplete"
          />
        </v-col>
        <v-col cols="12" md="6">
          <bm-dropdown
            v-model="internalTask.taskTypeId"
            :t="$m.keys.tasks_type"
            :items="cache.taskTypes.all"
            :sort-items="false"
            item-value="id"
            item-text="name"
            :disabled="taskIsCancelledOrComplete"
            :rules="[$ruleSet.required()]"
          />
        </v-col>
        <v-col cols="12" md="6">
          <bm-dropdown
            v-model="internalTask.taskQueueId"
            :t="$m.keys.queue"
            :items="cache.taskQueues.all"
            :sort-items="false"
            item-value="id"
            item-text="name"
            :disabled="taskIsCancelledOrComplete"
            :rules="[$ruleSet.required()]"
          />
        </v-col>
        <v-col cols="12">
          <v-divider />
        </v-col>
        <v-col cols="12" md="6">
          <bm-date
            v-model="internalTask.dueDate"
            :t="$m.keys.tasks_due_date"
            :disabled="taskIsCancelledOrComplete"
            clearable
          />
        </v-col>
        <v-col cols="12" md="6">
          <bm-time
            v-model="internalTask.dueTime"
            :t="$m.keys.tasks_due_time"
            :disabled="taskIsCancelledOrComplete"
            clearable
          />
        </v-col>
        <v-col cols="12" md="6">
          <bm-number
            v-model="durationMinutes"
            :t="$m.keys.task_type_duration"
            :placeholder="durationPlaceholder.toString()"
            :disabled="taskIsCancelledOrComplete"
            :rules="[$ruleSet.notNegativeNumber(), $ruleSet.notZero()]"
            :suffix="$m.units_mins_suffix"
            :min="1"
            :default-value="null"
            integral
            clearable
          />
        </v-col>
        <v-col cols="12">
          <v-divider />
        </v-col>
        <v-slide-x-transition>
          <v-col
            v-if="internalTask.dueDate && repeatParameterIsRelevant"
            cols="12"
            md="6"
          >
            <bm-number
              v-model="internalTask.taskRepeatParameter"
              :t="$m.keys.tasks_repeats_every"
              :min="1"
              :max="maxRepeatParameter"
              :disabled="taskIsCancelledOrComplete"
              :rules="[$ruleSet.numberInRange(1, maxRepeatParameter)]"
              integral
            />
          </v-col>
        </v-slide-x-transition>
        <v-slide-x-transition>
          <v-col v-if="internalTask.dueDate" cols="12" md="6">
            <bm-dropdown
              v-model="internalTask.taskRepeatBasis"
              :t="$m.keys.tasks_repeats"
              :items="EnumTaskRepeatBasis.getValues()"
              :item-text="(basis) => getRepeatBasisText(internalTask, basis)"
              :item-value="(basis) => basis.key"
              :sort-items="false"
              :searchable="false"
              :disabled="taskIsCancelledOrComplete"
              return-object
            />
          </v-col>
        </v-slide-x-transition>
        <v-col cols="12" md="6">
          <bm-enum-ddl
            v-model="internalTask.associatedBmEntityType"
            :t="$m.keys.tasks_associated_with"
            :enum-type="EnumBmEntityType"
            :enum-filter="availableEntityTypesFilter"
            :sorted-initial-value="EnumBmEntityType.none"
            :disabled="taskIsCancelledOrComplete"
          />
        </v-col>
        <v-slide-x-transition>
          <v-col
            v-if="associatedDependencies && !fetchingAssociatedItems"
            cols="12"
            md="6"
          >
            <bm-dropdown
              v-model="internalTask.associatedBmEntityId"
              :t="$m.keys.tasks_record"
              :items="associatedItems"
              :item-text="associatedDependencies.getText"
              :item-value="associatedDependencies.getValue"
              :disabled="taskIsCancelledOrComplete"
            />
          </v-col>
        </v-slide-x-transition>
        <v-col cols="12" md="6">
          <bm-dropdown
            v-model="internalTask.assignedToUserId"
            :t="$m.keys.tasks_assigned_to"
            :items="users"
            item-value="id"
            item-text="name"
            :disabled="taskIsCancelledOrComplete"
            clearable
          />
        </v-col>
        <v-col cols="12">
          <bm-text-area
            v-model="internalTask.notes"
            :t="$m.keys.tasks_notes"
            :disabled="taskIsCancelledOrComplete"
          />
        </v-col>
      </v-row>
    </v-container>
    <template #controls>
      <wrapped-control :control="btnBar"></wrapped-control>
    </template>
  </bm-dialog>
</template>

<script setup lang="ts">
import type { BmDialogInterface } from "@/components/dialogs/BmDialog.vue";
import BmDialog from "@/components/dialogs/BmDialog.vue";
import api from "@/app/api";
import type { User } from "@/app/services/generated/userService";
import type { BmTask } from "@/app/services/generated/bmtaskService";
import { bmTaskService } from "@/app/services/generated/bmtaskService";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import { EnumTaskRepeatBasis } from "@/app/services/generated/enums/enumTaskRepeatBasis.generated";
import {
  makeDeepRef,
  makeRef,
  withLoading,
  wrapPromiseWithErrorHandling,
} from "@/components/composables/componentTools";
import type { DialogEvents } from "@/components/mixins/dialogs";
import { useReactiveDialogScriptSetup } from "@/components/mixins/dialogs";
import {
  computed,
  toRef,
  shallowRef,
  onBeforeMount,
  watch,
  ref,
  watchEffect,
} from "vue";
import Guid from "@/app/types/common/guid";
import type { Associations } from "@/components/pages/tasks/composables/taskAssociations";
import {
  getAssociationFromType,
  availableEntityTypesFilter,
} from "@/components/pages/tasks/composables/taskAssociations";
import userStore from "@/store/modules/userStore";
import tasksStore from "@/store/modules/tasksStore";
import { useI18n } from "@/plugins/i18n";
import { userService } from "@/app/services/extensions/userServiceExtended";
import { getRepeatBasisText } from "@/components/pages/tasks/composables/tasks";
import { EnumTaskStatus } from "@/app/services/generated/enums/enumTaskStatus.generated";
import BmAlert from "@/components/controls/BmAlert.vue";
import { buildCache } from "@/app/caching/buildCache";
import { buttons } from "@/controls/buttons";
import { WrappedControl } from "@/controls/control";

interface AddEditTaskDialogProps {
  value: boolean;
  addOrEditTask: BmTask;
}

const props = defineProps<AddEditTaskDialogProps>();

export interface AddEditTaskDialogEvents extends DialogEvents {
  (event: "cancel-task", task: BmTask): void;
  (event: "complete-task", task: BmTask): void;
  (event: "save-and-duplicate", task: BmTask): void;
  (event: "save-and-create", task: BmTask): void;
}

const emit = defineEmits<AddEditTaskDialogEvents>();

const { $t, $m } = useI18n();
const internalTask = makeDeepRef(props.addOrEditTask.clone());
const durationMinutes = ref<number | null>(null);

const { accept, cancel } = useReactiveDialogScriptSetup(
  toRef(props, "value"),
  emit
);

const bmDialogRef = ref<BmDialogInterface | null>(null);

const cache = buildCache()
  .withTaskTypes()
  .withTaskQueues()
  .withOutletSummaries()
  .withSupplierManufacturers()
  .withGroups()
  .withItems()
  .withPriceLists()
  .withVessels()
  .build();

const users = makeRef<readonly User[]>([]);
const isNewTask = computed(() => internalTask.value.version === 0);

const taskIsCancelledOrComplete = computed(
  () =>
    internalTask.value.calculated.status === EnumTaskStatus.cancelled ||
    internalTask.value.calculated.status === EnumTaskStatus.complete
);

const taskCancelledOrCompleteAlertText = computed(() => {
  if (internalTask.value.calculated.status === EnumTaskStatus.cancelled) {
    return $m.tasks_task_cannot_edit_cancelled;
  }

  if (internalTask.value.calculated.status === EnumTaskStatus.complete) {
    return $m.tasks_task_cannot_edit_complete;
  }

  return "";
});

const associatedDependencies = shallowRef<Associations | null>(null);
const associatedItems = shallowRef<readonly any[]>([]);

const maxRepeatParameter = computed(() => {
  if (
    internalTask.value.taskRepeatBasis ===
    EnumTaskRepeatBasis.nthMatchingDayOfEachMonth
  ) {
    return 4;
  }

  return undefined;
});

const repeatParameterIsRelevant = computed(() => {
  return (
    internalTask.value.taskRepeatBasis === EnumTaskRepeatBasis.everyNWeeks ||
    internalTask.value.taskRepeatBasis === EnumTaskRepeatBasis.everyNMonths ||
    internalTask.value.taskRepeatBasis ===
      EnumTaskRepeatBasis.nthMatchingDayOfEachMonth
  );
});

const durationPlaceholder = computed(() => {
  const taskTypeId = internalTask.value.taskTypeId;
  const defaultDuration = 30;

  return (
    cache.taskTypes.allReactive.value.find((type) => type.id.equals(taskTypeId))
      ?.durationMinutes ?? defaultDuration
  );
});

const saveTask = async () => {
  internalTask.value.durationMinutes =
    durationMinutes.value ?? durationPlaceholder.value;

  await bmTaskService.saveBmTask(api.tenantId, internalTask.value);
};

const addOrUpdateTask = withLoading(async () => {
  await saveTask();
  await tasksStore.refresh();
  accept();
});

// Handle the canceling/completing internally, but push an event to listen to if we're on the page
const cancelTask = withLoading(async () => {
  await bmTaskService.cancelBmTask(api.tenantId, props.addOrEditTask.id);
  await tasksStore.refresh();
  emit("cancel-task", props.addOrEditTask);
  cancel();
});

const completeTask = withLoading(async () => {
  await bmTaskService.completeBmTask(api.tenantId, props.addOrEditTask.id);
  await tasksStore.refresh();
  emit("complete-task", props.addOrEditTask);
  cancel();
});

const saveAndDuplicate = withLoading(async () => {
  await saveTask();
  await tasksStore.refresh();
  emit("save-and-duplicate", internalTask.value);
  internalTask.value.id = Guid.create();
  internalTask.value.version = 0;
});

const saveAndCreate = withLoading(async () => {
  await saveTask();
  await tasksStore.refresh();
  emit("save-and-create", props.addOrEditTask);
  internalTask.value = props.addOrEditTask.clone();
  internalTask.value.version = 0;
  setTaskDefaults();
});

const setTaskDefaults = () => {
  internalTask.value.id = Guid.create();
  internalTask.value.assignedToUserId = userStore.state.user!.id;
  durationMinutes.value = null;

  // task type
  if (internalTask.value.taskTypeId.isEmpty()) {
    const taskTypes = cache.taskTypes.all.filter((type) => !type.isHidden);

    if (taskTypes.length > 0) {
      internalTask.value.taskTypeId = taskTypes[0].id;
    }
  }

  // task queue
  if (internalTask.value.taskQueueId.isEmpty()) {
    const taskQueues = cache.taskQueues.all.filter((type) => !type.isHidden);

    if (taskQueues.length > 0) {
      internalTask.value.taskQueueId = taskQueues[0].id;
    }
  }
};

const cancelTaskBtn = buttons.secondary($m.tasks_cancel);
cancelTaskBtn.onClick = cancelTask;

const completeTaskBtn = buttons.secondary($m.tasks_complete_task);
completeTaskBtn.onClick = completeTask;

const saveAndCreateBtn = buttons.secondary($m.tasks_save_and_create);
saveAndCreateBtn.onClick = saveAndCreate;

const saveAndDuplicateBtn = buttons.secondary($m.tasks_save_and_duplicate);
saveAndDuplicateBtn.onClick = saveAndDuplicate;

const saveBtn = buttons.primary($m.save);
saveBtn.onClick = addOrUpdateTask;

const cancelBtn = buttons.secondary($m.cancel);
cancelBtn.onClick = cancel;

const btnBar = buttons.dialogButtonLayout({
  primary: [saveBtn],
  right: [
    cancelTaskBtn,
    completeTaskBtn,
    saveAndCreateBtn,
    saveAndDuplicateBtn,
  ],
  left: [cancelBtn],
});

watchEffect(() => {
  const canSave =
    !!bmDialogRef.value?.isValid() && !taskIsCancelledOrComplete.value;
  cancelTaskBtn.in.visible =
    !isNewTask.value && !taskIsCancelledOrComplete.value;

  completeTaskBtn.in.visible =
    !isNewTask.value && !taskIsCancelledOrComplete.value;

  saveAndCreateBtn.in.visible = isNewTask.value;
  saveAndCreateBtn.in.enabled = canSave;

  saveAndDuplicateBtn.in.visible = isNewTask.value;
  saveAndDuplicateBtn.in.enabled = canSave;

  saveBtn.in.text = isNewTask.value ? $m.save : $m.update;
  saveBtn.in.enabled = canSave;
});

// prevent bm-dropdown from passing a callback of a different type to
// item-text and item-value while fetching associated dependencies (prevent
// undefined errors when switching association types)
const fetchingAssociatedItems = ref(false);

watch(
  () => internalTask.value.associatedBmEntityType,
  withLoading(
    wrapPromiseWithErrorHandling(async () => {
      fetchingAssociatedItems.value = true;
      await cache.update();

      associatedDependencies.value = getAssociationFromType(
        internalTask.value.associatedBmEntityType,
        cache
      );

      if (associatedDependencies.value) {
        associatedItems.value = await associatedDependencies.value.getAll({
          initialItemId: internalTask.value.associatedBmEntityId,
        });
      }
      fetchingAssociatedItems.value = false;
    })
  ),
  { immediate: true }
);

watch(
  () => internalTask.value.associatedBmEntityType,
  () => (internalTask.value.associatedBmEntityId = null)
);

onBeforeMount(
  withLoading(async () => {
    users.value = await userService.getAllUsersWithPremierUsersHidden(
      api.tenantGroupId
    );

    if (isNewTask.value) {
      internalTask.value.taskRepeatParameter = 1;
      setTaskDefaults();
    }
  })
);

watch(
  () => internalTask.value.durationMinutes,
  () => {
    if (internalTask.value.durationMinutes !== 0) {
      durationMinutes.value = internalTask.value.durationMinutes;
    }
  },
  {
    immediate: true,
  }
);
</script>
